import { AppContext, BookingData, ServiceDuration, TimeSlot } from "../../../../AppDataProvider/AppDataProvider";
import { Button, LoadingSpinner } from "../../../../common/components";
import { corners, marginBottom, marginLeft, marginRight, media, paddingBottom, paddingX, typography } from "../../../../common/styles";
import { faChevronRight, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { getCurrencyDisplay, requireBookingDataFields, useViewportSize } from "../../../../utils";
import React, { useContext, useState } from "react";
import styled, { useTheme } from "styled-components";
import { applyOpacity } from "../../../../common/styles/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PartRequired } from "../../../../utils/types";
import { useTranslation } from "react-i18next";
type AvailabilityStepBookingData = PartRequired<BookingData, "organisation" | "service" | "timezone">;
type CompleteStepOpts = {
  attendees: number;
  duration: ServiceDuration;
  selectedTimeSlot: TimeSlot;
};
type IntervalListItemProps = {
  attendees: number;
  createReservationLoading: boolean;
  duration: ServiceDuration;
  onCompleteStep: (options: CompleteStepOpts) => void;
  slot: TimeSlot;
};
export function IntervalListItem({
  attendees,
  duration,
  createReservationLoading,
  slot,
  onCompleteStep
}: IntervalListItemProps): React.ReactElement {
  const viewport = useViewportSize();
  const {
    t,
    i18n
  } = useTranslation();
  const theme = useTheme();
  const {
    appData
  } = useContext(AppContext);
  const [selectedSlotDate, setSelectedSlotDate] = useState<string | undefined>();
  requireBookingDataFields<AvailabilityStepBookingData>(appData.bookingData, ["service", "timezone", "organisation"]);
  const {
    timezone,
    organisation,
    service
  } = appData.bookingData;
  const {
    percentageThresholdForShowingRemainingNumberOfSpaces
  } = appData.bookingTool;
  const occupancy = service.occupancy ?? 500;
  const {
    interval
  } = slot;
  const dayDisplay = new Intl.DateTimeFormat(i18n.language, {
    day: "2-digit",
    timeZone: timezone
  }).format(new Date(interval.start));
  const weekdayDisplay = new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezone,
    weekday: "short"
  }).format(new Date(interval.start));
  const slotStart = new Intl.DateTimeFormat(i18n.language, {
    timeStyle: "short",
    timeZone: timezone
  }).format(new Date(interval.start));
  const isGroupBooking = interval.__typename === "GroupBookingInterval" && interval.booking;
  const isRecurringBooking = interval.__typename === "GroupBookingInterval" && interval.booking.recurring;
  const bookingSpecificPrice = interval.__typename === "GroupBookingInterval" && interval.booking && interval.booking.price ? interval.booking.price : 0;
  const totalBookingSpecificPrice = bookingSpecificPrice * attendees;
  const totalSpecificPriceDisplay = totalBookingSpecificPrice > 0 ? getCurrencyDisplay(totalBookingSpecificPrice, organisation.currency, i18n.language) : t("free");
  const getTotalPriceDisplay = (duration: ServiceDuration): string => {
    const price = duration.price ?? 0;
    const totalPrice = price * attendees;
    return totalPrice === 0 ? t("free") : getCurrencyDisplay(totalPrice, organisation.currency, i18n.language);
  };
  const totalDurationPrice = getTotalPriceDisplay(duration);
  const selectSlot = () => {
    setSelectedSlotDate(slot.interval.start);
    onCompleteStep({
      attendees,
      duration,
      selectedTimeSlot: slot
    });
  };
  const slotAriaLabel = new Intl.DateTimeFormat(i18n.language, /* eslint-disable sort-keys */{
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone: timezone
  } /* eslint-enable sort-keys */).format(new Date(interval.start));

  /**
   * If there is no threshold defined we will always show the remaining spaces
   * on a group booking.
   *
   * If a threshold is defined we will only show remaining spaces
   * when the number of spaces left on a booking drops below that threshold.
   *
   * e.g. Threshold is set to 60% on a booking that can have 100 spaces.
   * when the number of remaining spaces is 60 or less we will display
   * the remaining spaces.
   */
  const calculateShowSpacesThreshold = () => {
    if (percentageThresholdForShowingRemainingNumberOfSpaces === null || percentageThresholdForShowingRemainingNumberOfSpaces === undefined) {
      return Infinity;
    }
    return Math.floor(occupancy / 100 * percentageThresholdForShowingRemainingNumberOfSpaces);
  };
  const spacesThreshold = calculateShowSpacesThreshold();
  const spacesLeft = interval.remainingSpaces ?? occupancy;
  const showSpacesLeft = spacesLeft <= spacesThreshold;
  const showLoadingSpinner = createReservationLoading && selectedSlotDate === slot.interval.start;
  function getButtonContent(): React.ReactNode {
    if (showLoadingSpinner) return <LoadingSpinner colour={theme.colours.textOnPrimary} />;
    if (viewport.width >= media.thresholds.tablet) return t("select");
    return <SelectSlotIcon icon={faChevronRight} data-sentry-element="SelectSlotIcon" data-sentry-component="getButtonContent" data-sentry-source-file="IntervalListItem.tsx" />;
  }
  return <ListItem onClick={selectSlot} key={slot.interval.start} data-sentry-element="ListItem" data-sentry-component="IntervalListItem" data-sentry-source-file="IntervalListItem.tsx">
      <Info data-sentry-element="Info" data-sentry-source-file="IntervalListItem.tsx">
        <Day data-sentry-element="Day" data-sentry-source-file="IntervalListItem.tsx">{dayDisplay}</Day>
        <Weekday data-sentry-element="Weekday" data-sentry-source-file="IntervalListItem.tsx">{weekdayDisplay}</Weekday>
        <TimeAndSpaces data-sentry-element="TimeAndSpaces" data-sentry-source-file="IntervalListItem.tsx">
          <Time data-sentry-element="Time" data-sentry-source-file="IntervalListItem.tsx">
            {isRecurringBooking ? <RecurringIcon icon={faSyncAlt} aria-label={t("recurringIconLabel")} /> : null}
            {slotStart}
          </Time>
          {showSpacesLeft && <Spaces>
              <SpacesLabel>{t("spacesLeft")}</SpacesLabel>
              {spacesLeft}
            </Spaces>}
        </TimeAndSpaces>
      </Info>
      <PriceAndSelect data-sentry-element="PriceAndSelect" data-sentry-source-file="IntervalListItem.tsx">
        {isGroupBooking && totalSpecificPriceDisplay !== totalDurationPrice && <Price>{totalSpecificPriceDisplay}</Price>}
        <SelectSlotButton aria-label={slotAriaLabel} data-sentry-element="SelectSlotButton" data-sentry-source-file="IntervalListItem.tsx">
          {getButtonContent()}
        </SelectSlotButton>
      </PriceAndSelect>
    </ListItem>;
}
const ListItem = styled.li`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  &:not(:last-child) {
    ${paddingBottom.m}
    border-bottom: 1px solid
      ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
    ${marginBottom.m}
  }

  @media ${media.hoverable} {
    &:hover {
      cursor: pointer;
    }
  }
`;
const Info = styled.div`
  display: flex;
  align-items: center;
`;
const Day = styled.div`
  flex-shrink: 0;
  height: 2.5rem;
  width: 2.5rem;
  ${corners.m}
  ${marginRight.s}

  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.05)};
  ${typography.bodyBold};
`;
const Weekday = styled.div`
  ${typography.body}
  min-width: 3rem;
`;
const TimeAndSpaces = styled.div`
  display: flex;

  @media ${media.mobile} {
    flex-direction: column;
  }
`;
const RecurringIcon = styled(FontAwesomeIcon)`
  ${marginRight.s}
  color: ${({
  theme
}) => theme.colours.primaryText};
`;
const Time = styled.div`
  display: flex;
  align-items: center;
  ${marginRight.m}
  ${typography.body};
`;
const Spaces = styled.div`
  display: flex;
  ${typography.small};
  align-items: center;
`;
const SpacesLabel = styled.div`
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  ${marginRight.s}
  &::first-letter {
    text-transform: capitalize;
  }
`;
const PriceAndSelect = styled.div`
  display: flex;
  align-items: center;
`;
const Price = styled.div`
  ${typography.smallBold};
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
  align-items: center;
`;
const SelectSlotButton = styled(Button)`
  ${marginLeft.m}
  ${paddingX.m}
`;
const SelectSlotIcon = styled(FontAwesomeIcon)`
  font-size: 1.125rem;
`;